import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import recallService from '../../../services/recall.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import ActivitySkeleton from '../activity/subparts/ActivitySkeleton';
import { formatActivityCount } from '../activity/subparts/activityQuery';
import '../activity/Activity.css';
import './RecallPools.css';
import RecallPoolEditModal from './RecallPoolEditModal';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const SEARCH_FIELDS = [
    { value: 'lastName', label: 'Last name', placeholder: 'Search by last name' },
    { value: 'firstName', label: 'First name', placeholder: 'Search by first name' },
    { value: 'email', label: 'Email', placeholder: 'Search by email' },
    { value: 'cellNumber', label: 'Cell number', placeholder: 'Search by cell number' },
];

const STATUS_OPTIONS = [
    { value: 'ALL', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROCESS', label: 'In process' },
    { value: 'SENT', label: 'Sent' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'COMPLETED', label: 'Completed' },
];

const MEDIUM_OPTIONS = [
    { value: 'ALL', label: 'All' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'SMS', label: 'SMS' },
    { value: 'CALL', label: 'Call' },
];

const SORT_FIELDS = [
    { value: 'createdAt', label: 'Created date' },
    { value: 'modifiedAt', label: 'Modified date' },
    { value: 'lastName', label: 'Last name' },
    { value: 'firstName', label: 'First name' },
    { value: 'status', label: 'Status' },
];

const DEFAULT_FILTERS = {
    searchField: 'lastName',
    searchTerm: '',
    status: 'ALL',
    mediumType: 'ALL',
};

const DEFAULT_SORT = {
    sortBy: 'createdAt',
    sortOrder: 'DESC',
};

const STATUS_META = {
    PENDING: { className: 'activity-status--waiting', icon: 'bi-hourglass-split', label: 'Pending' },
    IN_PROCESS: { className: 'activity-status--resend', icon: 'bi-arrow-repeat', label: 'In process' },
    SENT: { className: 'activity-status--sent', icon: 'bi-check-circle-fill', label: 'Sent' },
    FAILED: { className: 'activity-status--failed', icon: 'bi-x-circle-fill', label: 'Failed' },
    COMPLETED: { className: 'recall-pool-status--completed', icon: 'bi-check2-all', label: 'Completed' },
};

const MEDIUM_META = {
    EMAIL: { icon: 'bi-envelope-fill', className: 'activity-medium--email' },
    SMS: { icon: 'bi-chat-text-fill', className: 'activity-medium--sms' },
    CALL: { icon: 'bi-telephone-fill', className: 'activity-medium--call' },
};

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.PENDING;

    return (
        <span className={`activity-status ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {meta.label}
        </span>
    );
}

function MediumBadge({ mediumType }) {
    const meta = MEDIUM_META[mediumType] || { icon: 'bi-question-circle', className: 'activity-medium--default' };

    return (
        <span className={`activity-medium ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {mediumType || 'Unknown'}
        </span>
    );
}

function RecallPools() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [editingPool, setEditingPool] = useState(null);

    const fetchPools = useCallback(
        async (page, size, nextFilters, nextSort, options = {}) => {
            const { silent = false } = options;
            if (!clinicId) return;

            if (!silent) setLoading(true);

            try {
                const response = await recallService.getRecallPools(clinicId, page, size, {
                    sortBy: nextSort.sortBy,
                    sortOrder: nextSort.sortOrder,
                    searchField: nextFilters.searchField,
                    searchTerm: nextFilters.searchTerm,
                    statusFilter: nextFilters.status,
                    mediumType: nextFilters.mediumType,
                });
                const data = response.data;
                setRecords(data.content || []);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
            } catch (error) {
                console.error('Failed to load recall pools', error);
                toast.error('Failed to load recall pools');
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [clinicId]
    );

    useEffect(() => {
        fetchPools(pageNumber, pageSize, appliedFilters, sort);
    }, [fetchPools, pageNumber, pageSize, appliedFilters, sort]);

    const searchMeta = useMemo(
        () => SEARCH_FIELDS.find((field) => field.value === filters.searchField) || SEARCH_FIELDS[0],
        [filters.searchField]
    );

    const description = useMemo(
        () =>
            totalElements
                ? `Patients in your clinic recall pool · ${formatActivityCount(totalElements)} total record${totalElements === 1 ? '' : 's'}`
                : 'Search and review patients in your clinic recall pool',
        [totalElements]
    );

    const applyFilters = (nextFilters = filters) => {
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setPageNumber(0);
    };

    const handleQuickFilter = (field, value) => {
        const next = { ...filters, [field]: value };
        applyFilters(next);
    };

    const handleClear = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
        setSort(DEFAULT_SORT);
        setPageNumber(0);
    };

    const hasActiveFilters =
        Boolean(appliedFilters.searchTerm?.trim()) ||
        appliedFilters.status !== 'ALL' ||
        appliedFilters.mediumType !== 'ALL';

    const handleSortChange = (field, value) => {
        setSort((prev) => ({ ...prev, [field]: value }));
        setPageNumber(0);
    };

    const handleRefresh = async () => {
        if (!clinicId || isRefreshing) return;
        setIsRefreshing(true);
        try {
            await fetchPools(pageNumber, pageSize, appliedFilters, sort, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            applyFilters();
        }
    };

    return (
        <div className="ecx-page activity-page ecx-page--full-width recall-pools-page">
            <SubHeader
                title="Recall Pool"
                description={description}
                icon="bi-people"
                containerClass="w-100 px-4"
            >
                <div className="d-flex align-items-center gap-2">
                    <Link to="/help?category=recalls" className="btn btn-sm btn-outline-primary rounded-pill">
                        <i className="bi bi-question-circle me-1"></i>
                        Help
                    </Link>
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleRefresh}
                        disabled={isRefreshing || loading}
                        title="Refresh recall pool"
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                        Refresh
                    </button>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel activity-log-panel">
                    <div className="activity-filter-panel">
                        <div className="activity-filter-panel__toolbar">
                            <div className="activity-filter-panel__search">
                                <select
                                    className="form-select form-select-sm activity-filter-panel__field"
                                    value={filters.searchField}
                                    onChange={(event) =>
                                        setFilters((prev) => ({ ...prev, searchField: event.target.value }))
                                    }
                                    disabled={loading}
                                    aria-label="Search by"
                                >
                                    {SEARCH_FIELDS.map((field) => (
                                        <option key={field.value} value={field.value}>
                                            {field.label}
                                        </option>
                                    ))}
                                </select>

                                <div className="input-group input-group-sm activity-filter-panel__query">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm border-start-0"
                                        placeholder={searchMeta.placeholder}
                                        value={filters.searchTerm}
                                        onChange={(event) =>
                                            setFilters((prev) => ({ ...prev, searchTerm: event.target.value }))
                                        }
                                        onKeyDown={handleSearchKeyDown}
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-accent"
                                        onClick={() => applyFilters()}
                                        disabled={loading}
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>

                            <div className="activity-filter-panel__quick">
                                <select
                                    className="form-select form-select-sm activity-filter-panel__select"
                                    value={filters.mediumType}
                                    onChange={(event) => handleQuickFilter('mediumType', event.target.value)}
                                    disabled={loading}
                                    aria-label="Medium"
                                >
                                    {MEDIUM_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            Medium: {option.label}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="form-select form-select-sm activity-filter-panel__select"
                                    value={filters.status}
                                    onChange={(event) => handleQuickFilter('status', event.target.value)}
                                    disabled={loading}
                                    aria-label="Status"
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            Status: {option.label}
                                        </option>
                                    ))}
                                </select>

                                {hasActiveFilters && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleClear}
                                        disabled={loading}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__header border-top-0">
                        <div className="activity-toolbar">
                            <div className="activity-toolbar__meta">
                                <h5 className="mb-0 fw-normal text-dark">Recall pool</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul"></i>
                                    Page {totalPages === 0 ? 0 : pageNumber + 1} of {Math.max(totalPages, 1)}
                                </span>
                            </div>

                            <div className="activity-toolbar__controls">
                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Sort
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '9.5rem' }}
                                        value={sort.sortBy}
                                        onChange={(event) => handleSortChange('sortBy', event.target.value)}
                                        disabled={loading}
                                    >
                                        {SORT_FIELDS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '6.5rem' }}
                                        value={sort.sortOrder}
                                        onChange={(event) => handleSortChange('sortOrder', event.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="DESC">Desc</option>
                                        <option value="ASC">Asc</option>
                                    </select>
                                </label>

                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value));
                                            setPageNumber(0);
                                        }}
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    entries
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column">
                        {loading ? (
                            <ActivitySkeleton count={pageSize > 5 ? 5 : pageSize} />
                        ) : records.length > 0 ? (
                            <>
                                <div className="activity-date-group__items flex-grow-1">
                                    {records.map((item) => {
                                        const name = `${item.firstName || ''} ${item.lastName || ''}`.trim() || '—';
                                        const contact = item.email || item.cellNumber || item.phoneNumber || 'No contact';

                                        return (
                                            <article
                                                className="activity-item ecx-page-list-item recall-pool-row"
                                                key={item.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setEditingPool(item)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        setEditingPool(item);
                                                    }
                                                }}
                                            >
                                                <div className="activity-item__row">
                                                    <span className="ecx-page-icon-badge activity-item__icon">
                                                        <i className={`bi ${MEDIUM_META[item.mediumType]?.icon || 'bi-person'}`}></i>
                                                    </span>
                                                    <div className="activity-item__title" title={name}>
                                                        {name}
                                                        <span className="text-muted ms-2">{contact}</span>
                                                    </div>
                                                    <div className="activity-item__meta">
                                                        <StatusBadge status={item.status} />
                                                        <MediumBadge mediumType={item.mediumType} />
                                                        <time className="activity-item__time" title={item.createdAt || undefined}>
                                                            {item.createdAt || '—'}
                                                        </time>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top pt-3 mt-3">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={setPageNumber}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="ecx-page-empty flex-grow-1">
                                <div className="ecx-page-empty__icon">
                                    <i className="bi bi-people"></i>
                                </div>
                                <h5 className="text-dark fw-normal mb-2">No recall pool entries found</h5>
                                <p className="mb-0">Try adjusting your search or status filters.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <RecallPoolEditModal
                show={Boolean(editingPool)}
                clinicId={clinicId}
                poolId={editingPool?.id}
                patientName={`${editingPool?.firstName || ''} ${editingPool?.lastName || ''}`.trim()}
                onClose={() => setEditingPool(null)}
                onSaved={() => fetchPools(pageNumber, pageSize, appliedFilters, sort, { silent: true })}
            />
        </div>
    );
}

export default RecallPools;
